<template>
    <v-dialog v-model="open" max-width="640" :persistent="busy" scrollable>
        <template #activator="{ on, attrs }">
            <file-upload-tile :width="width" :height="height" :compact="compact" :dragging="tileDragging" :loading="busy"
                v-bind="attrs" v-on="on" @dragover.prevent="tileDragging = !busy"
                @dragleave.prevent="tileDragging = false" @drop.prevent.stop="dropFiles" />
        </template>
        <div class="mc-root upload-dialog" @dragover.prevent @drop.prevent>
            <v-card class="upload-card" :aria-busy="String(busy)">
                <v-card-title class="upload-title">Carica file</v-card-title>
                <v-card-text class="upload-body">
                    <p class="mc-caption">Articolo <span class="mc-code">{{ articolo }}</span></p>
                    <button type="button" class="upload-drop" :class="{ 'is-dragging': dragDepth > 0 }"
                        :disabled="busy" @click="chooseFiles" @dragenter.prevent="dragDepth += 1"
                        @dragover.prevent @dragleave.prevent="dragDepth = Math.max(0, dragDepth - 1)"
                        @drop.prevent.stop="dropFiles">
                        <v-icon aria-hidden="true">mdi-tray-arrow-up</v-icon>
                        <strong>{{ dragDepth ? 'Rilascia qui i file' : 'Trascina qui uno o più file' }}</strong>
                        <span>oppure fai clic per selezionarli</span>
                        <span class="mc-caption">PDF, PNG e JPEG · meno di 10 MB per file</span>
                    </button>
                    <input ref="picker" type="file" multiple :accept="accept" hidden :disabled="busy" @change="selectFiles" />
                    <p class="mc-caption upload-summary" role="status" aria-live="polite">{{ summary }}</p>
                    <ul v-if="entries.length" class="upload-list" aria-label="File selezionati">
                        <li v-for="entry in entries" :key="entry.key" class="upload-row">
                            <div class="upload-file">
                                <strong class="upload-name">{{ entry.file.name }}</strong>
                                <span class="mc-caption">{{ sizeLabel(entry.file.size) }} · {{ statusLabel(entry) }}</span>
                                <p v-if="entry.error" class="upload-error" role="alert">{{ entry.error }}</p>
                                <v-progress-linear v-if="entry.status === 'uploading'" :value="entry.progress"
                                    :indeterminate="entry.progress === 100" aria-label="Caricamento file" />
                            </div>
                            <v-btn v-if="!['success', 'uncertain'].includes(entry.status)" icon :disabled="busy"
                                :aria-label="`Rimuovi ${entry.file.name}`" @click="remove(entry)"><v-icon>mdi-close</v-icon></v-btn>
                            <v-icon v-else aria-hidden="true">{{ entry.status === 'success' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                        </li>
                    </ul>
                    <v-checkbox v-model="imballaggio" dense hide-details :disabled="busy || attempted"
                        label="Sono file di imballaggio" />
                </v-card-text>
                <v-card-actions class="upload-actions">
                    <v-btn text :disabled="busy" @click="open = false">{{ attempted ? 'Chiudi' : 'Annulla' }}</v-btn>
                    <v-spacer />
                    <v-btn color="primary" depressed :disabled="busy || !pendingCount" :loading="busy" @click="upload">
                        {{ attempted ? 'Carica rimanenti' : 'Carica' }}<template v-if="pendingCount"> ({{ pendingCount }})</template>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </div>
    </v-dialog>
</template>

<script>
import FileUploadTile from '../FileUploadTile.vue';
import { number } from '@/assets/mecom/js/format.mjs';
import { addFiles, canUpload, FILE_ACCEPT, uploadEntries } from './uploadQueue.mjs';

export default {
    name: 'ArticleFileUpload',
    components: { FileUploadTile },
    props: {
        compact: { type: Boolean, default: false },
        width: Number, height: Number, articolo: String, fase: String,
        operazione: [Number, String], distintaAlternativa: [Number, String],
    },
    data: () => ({ open: false, busy: false, entries: [], imballaggio: false, attempted: false,
        tileDragging: false, dragDepth: 0, disposed: false, accept: FILE_ACCEPT }),
    computed: {
        pendingCount() { return this.entries.filter(canUpload).length; },
        summary() {
            if (!this.entries.length) return 'Nessun file selezionato.';
            const done = this.entries.filter(entry => entry.status === 'success').length;
            return this.attempted ? `${done} di ${this.entries.length} file caricati` : `${this.entries.length} file selezionati`;
        },
    },
    watch: {
        open(value) {
            if (!value && !this.busy) {
                this.entries = []; this.imballaggio = false; this.attempted = false; this.dragDepth = 0;
            }
        },
    },
    beforeDestroy() { this.disposed = true; },
    methods: {
        sizeLabel(bytes) { return bytes < 1000000 ? `${number(bytes / 1000, 1)} kB` : `${number(bytes / 1000000, 1)} MB`; },
        statusLabel(entry) {
            return { pending: 'Da caricare', invalid: 'Non valido', success: 'Caricato', error: 'Non caricato',
                uncertain: 'Da verificare', uploading: entry.progress === 100 ? 'Salvataggio…' : `Caricamento ${entry.progress}%` }[entry.status];
        },
        chooseFiles() { if (!this.busy) this.$refs.picker.click(); },
        selectFiles(event) { this.append(event.target.files); event.target.value = ''; },
        append(files) { if (!this.busy) this.entries = addFiles(this.entries, files); },
        dropFiles(event) {
            this.tileDragging = false; this.dragDepth = 0;
            if (this.busy || !event.dataTransfer?.files?.length) return;
            this.open = true;
            this.append(event.dataTransfer.files);
        },
        remove(entry) { if (!this.busy) this.entries = this.entries.filter(item => item !== entry); },
        async upload() {
            if (this.busy || !this.pendingCount) return;
            this.busy = true; this.attempted = true;
            try {
                await uploadEntries(this.entries, {
                    work: this.$work, context: { articolo: this.articolo, fase: this.fase,
                        operazione: this.operazione, distintaAlternativa: this.distintaAlternativa, imballaggio: this.imballaggio },
                    isActive: () => !this.disposed,
                    onUploaded: file => this.$emit('file-uploaded', file),
                });
                if (!this.disposed && this.entries.every(entry => entry.status === 'success')) {
                    this.$toast.success(this.summary, { icon: 'mdi-check-circle', timeout: 3000 });
                }
            } finally { this.busy = false; }
        },
    },
};
</script>

<style scoped>
.upload-dialog { min-width: 0; display: flex; max-height: 90vh; }
.upload-card { display: flex; flex-direction: column; width: 100%; min-width: 0; max-height: inherit; background: var(--surface); color: var(--ink); }
.upload-title { word-break: normal; flex-shrink: 0; }
.upload-body { overflow-y: auto; min-width: 0; min-height: 0; }
.upload-drop { display: flex; flex-direction: column; align-items: center; gap: var(--space-2); width: 100%; padding: var(--space-5); border: 1px dashed var(--brand); border-radius: var(--radius-md); background: var(--brand-soft); color: var(--brand-ink); text-align: center; cursor: pointer; }
.upload-drop .v-icon { color: inherit; }
.upload-drop > * { pointer-events: none; }
.upload-drop:hover, .upload-drop.is-dragging { background: var(--surface-2); border-style: solid; }
.upload-drop:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }
.upload-drop:disabled { cursor: wait; }
.upload-summary { margin: var(--space-3) 0; }
.upload-list { list-style: none; padding: 0; margin: 0; }
.upload-row { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) 0; border-bottom: 1px solid var(--border); }
.upload-file { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; flex: 1; }
.upload-name { overflow-wrap: anywhere; color: var(--ink); }
.upload-error { margin: 0; color: var(--bad-ink); }
.upload-actions { flex-wrap: wrap; flex-shrink: 0; gap: var(--space-2); padding: var(--space-3) var(--space-4); }
</style>
